// Client-side image resize + Base64 encode for offline storage in IndexedDB.
// Target: imagem final ≤ ~200KB para caber confortavelmente no Realtime DB
// (limite ~256KB/write) e não estourar a memória do Safari iOS no upload.

// Limite de entrada: 20MB. Fotos de iPhone Pro chegam a 12MB; aceitamos
// margem mas rejeitamos exageros (vídeo enviado por engano, RAW etc).
const MAX_INPUT_BYTES = 20 * 1024 * 1024;

export async function resizeImageToBase64(file, maxWidth = 600, quality = 0.82) {
  if (!file) throw new Error('Nenhum arquivo selecionado');
  if (!file.type.startsWith('image/')) {
    throw new Error('Selecione uma IMAGEM (jpg, png, heic) — não vídeo ou outro tipo.');
  }
  if (file.size > MAX_INPUT_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    throw new Error(`Foto muito grande (${mb} MB). Limite: 20 MB. Reduza no editor do celular antes de enviar.`);
  }

  // v7.25 — Usa createImageBitmap quando disponível (evita o passo intermediário
  // de base64 do FileReader, que duplica a memória usada). Fallback para
  // FileReader em browsers antigos.
  let bitmap;
  try {
    if (typeof createImageBitmap === 'function') {
      bitmap = await createImageBitmap(file);
    } else {
      const dataUrl = await readAsDataURL(file);
      bitmap = await loadImage(dataUrl);
    }
  } catch (err) {
    throw new Error('Não consegui ler a foto. Tire outra ou escolha da galeria.');
  }

  const srcW = bitmap.width || bitmap.naturalWidth;
  const srcH = bitmap.height || bitmap.naturalHeight;
  const ratio = srcW > maxWidth ? maxWidth / srcW : 1;
  const targetW = Math.round(srcW * ratio);
  const targetH = Math.round(srcH * ratio);

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  // Libera o bitmap o quanto antes (importante no Safari iOS).
  if (bitmap.close) {
    try { bitmap.close(); } catch (_) { /* close opcional */ }
  }

  // JPEG mesmo para PNG sem transparência — economiza ~60% do tamanho.
  // Mantemos PNG só se a fonte for PNG (provável que tenha alpha).
  const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  let base64 = canvas.toDataURL(mime, quality);

  // Guard final: se ainda passar de 220KB, recomprime mais agressivamente
  // até caber (cabe confortavelmente no Realtime DB).
  const MAX_OUTPUT_LEN = 220 * 1024;
  let attempt = 0;
  let currentQuality = quality;
  while (base64.length > MAX_OUTPUT_LEN && attempt < 3) {
    currentQuality = Math.max(0.45, currentQuality - 0.15);
    base64 = canvas.toDataURL('image/jpeg', currentQuality);
    attempt += 1;
  }

  // v7.26 — Métricas de compressão. O tamanho do base64 é ~1.33× o tamanho
  // real do binário; o que interessa pro DB é o tamanho da string mesmo.
  const meta = {
    originalBytes: file.size,
    finalBytes: base64.length,
    targetW,
    targetH,
    quality: currentQuality,
  };
  // Anexa as métricas como propriedades não-enumeráveis pra manter
  // compatibilidade com chamadas existentes que esperam só uma string.
  try {
    Object.defineProperty(base64, '_meta', { value: meta, enumerable: false });
  } catch (_) { /* string primitive — defineProperty falha em alguns engines */ }
  return base64;
}

/**
 * Wrapper que SEMPRE devolve { base64, meta } — use este quando precisar
 * das métricas de compressão (toast, log de auditoria).
 */
export async function resizeImageWithMetrics(file, maxWidth = 600, quality = 0.82) {
  const originalBytes = file?.size || 0;
  const base64 = await resizeImageToBase64(file, maxWidth, quality);
  return {
    base64,
    originalBytes,
    finalBytes: base64.length,
  };
}

/** Formata bytes em "1.2 MB" / "182 KB" / "4 bytes". */
export function formatBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return '0 bytes';
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} bytes`;
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Falha ao ler arquivo'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Imagem corrompida'));
    img.src = src;
  });
}

export function isBase64Image(url) {
  return typeof url === 'string' && url.startsWith('data:image/');
}

/**
 * v7.41 — Server-side image optimization.
 * POST multipart pra `/api/images/optimize` (Pillow + WebP).
 * Devolve `data:image/webp;base64,...` ≤ 200KB com qualidade superior ao
 * encoder do canvas (libwebp mozjpeg-equivalent, mesmo no Safari iOS).
 *
 * Lança em caso de falha (rede, 4xx, 5xx) — quem chama deve cair pro
 * fallback client-side (`resizeImageWithMetrics`).
 */
async function optimizeImageViaServer(file) {
  if (!file) throw new Error('Nenhum arquivo selecionado');
  if (!file.type.startsWith('image/')) {
    throw new Error('Selecione uma IMAGEM (jpg, png, heic) — não vídeo ou outro tipo.');
  }
  if (file.size > MAX_INPUT_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    throw new Error(`Foto muito grande (${mb} MB). Limite: 20 MB.`);
  }
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) throw new Error('Backend URL não configurada');

  const fd = new FormData();
  fd.append('file', file, file.name || 'image');
  // Timeout de 20s — fotos de iPhone Pro com upload 3G podem precisar.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  let resp;
  try {
    resp = await fetch(`${base}/api/images/optimize`, {
      method: 'POST',
      body: fd,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const j = await resp.json();
      if (j?.detail) detail = j.detail;
    } catch (_) { /* ignore */ }
    throw new Error(detail);
  }
  const json = await resp.json();
  if (!json?.base64DataUrl) throw new Error('Resposta inválida do servidor');
  return {
    base64: json.base64DataUrl,
    originalBytes: json.meta?.originalBytes || file.size,
    finalBytes: json.meta?.finalBytes || json.base64DataUrl.length,
    width: json.meta?.width,
    height: json.meta?.height,
    quality: json.meta?.quality,
    format: json.meta?.format || 'webp',
    source: 'server',
  };
}

/**
 * v7.41 — Otimização inteligente: tenta servidor (WebP melhor) e cai pro
 * client-side (JPEG via canvas) se a rede falhar. Usado pelo admin no
 * MenuAdminPage. Mantém a interface compatível com `resizeImageWithMetrics`.
 */
export async function optimizeImageSmart(file) {
  try {
    return await optimizeImageViaServer(file);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[IMAGE] server optimize falhou, usando fallback local:', err?.message || err);
    const { base64, originalBytes, finalBytes } = await resizeImageWithMetrics(file, 600, 0.82);
    return { base64, originalBytes, finalBytes, source: 'client' };
  }
}

/**
 * v7.43 — Converte um data URL (`data:image/webp;base64,...`) em Blob.
 * Usado para enviar a imagem otimizada como binário pro Firebase Storage
 * em vez de embutir o base64 no documento do cardápio.
 */
export function dataUrlToBlob(dataUrl) {
  if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
    throw new Error('dataUrlToBlob: input não é um data URL');
  }
  const commaIdx = dataUrl.indexOf(',');
  if (commaIdx === -1) throw new Error('dataUrlToBlob: data URL malformado');
  const header = dataUrl.slice(5, commaIdx); // remove "data:"
  const body = dataUrl.slice(commaIdx + 1);
  const isBase64 = header.endsWith(';base64');
  const mime = isBase64 ? header.slice(0, -7) : header;
  if (isBase64) {
    const bin = atob(body);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) buf[i] = bin.charCodeAt(i);
    return new Blob([buf], { type: mime || 'application/octet-stream' });
  }
  // Não-base64 (raro): decodificar URL-encoded.
  const text = decodeURIComponent(body);
  return new Blob([text], { type: mime || 'text/plain' });
}

/**
 * v7.43 — Pipeline completo de upload de imagem do cardápio.
 *
 * Fluxo feliz (Firebase Storage configurado, com auth ok):
 *   1. otimiza (server WebP ou client JPEG)
 *   2. converte base64 → Blob
 *   3. faz upload pro Storage `menuImages/{deviceGroup}/...`
 *   4. devolve `{ url: 'https://firebasestorage...', source: 'storage', ... }`
 *
 * Fallback (Storage offline / sem auth / sem bucket / rules denied / timeout):
 *   - Devolve `{ url: <base64>, source: 'base64', ... }` — compatível com
 *     o comportamento legado.
 *
 * v7.52 — Aceita callback `onProgress(stage, pct)` onde stage é
 * 'optimizing' (0..100) ou 'uploading' (0..100). Permite ao chamador
 * mostrar barra de progresso real (em vez de "Processando..." infinito).
 *
 * Importante: NUNCA lança em caso de falha de upload. O upload é
 * "best effort"; se cair, o operador pode salvar mesmo com base64
 * (que continua válido como <img src>).
 *
 * @param {File} file
 * @param {string} hint - nome do prato (vira parte do path no bucket)
 * @param {(stage: string, pct: number) => void} [onProgress]
 */
export async function uploadMenuImageSmart(file, hint = 'item', onProgress) {
  if (typeof onProgress === 'function') onProgress('optimizing', 0);
  const optimized = await optimizeImageSmart(file);
  const { base64, originalBytes, finalBytes, source } = optimized;
  if (typeof onProgress === 'function') onProgress('optimizing', 100);

  // Tenta subir pro Firebase Storage.
  try {
    // Import dinâmico evita ciclo de dependência com firebaseStorage.js
    // (que importa firebaseSync.js que importa db, etc).
    const { uploadMenuImageBlob } = await import('@/lib/firebaseStorage');
    const blob = dataUrlToBlob(base64);
    if (typeof onProgress === 'function') onProgress('uploading', 0);
    const up = await uploadMenuImageBlob(blob, hint, (pct) => {
      if (typeof onProgress === 'function') onProgress('uploading', pct);
    });
    return {
      url: up.url,
      source: 'storage',          // foi pro bucket
      optimizeSource: source,     // 'server' ou 'client' (como foi comprimida)
      originalBytes,
      finalBytes,
      storagePath: up.path,
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[IMAGE] Storage upload falhou, usando base64 inline:', err?.message || err);
    return {
      url: base64,
      source: 'base64',
      optimizeSource: source,
      originalBytes,
      finalBytes,
      uploadError: err?.message || String(err),
    };
  }
}
